import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReviewRecipe } from '../interfaces/IReviewRecipe';
import { findRecipeById } from '../interfaces/IReviewRecipe';

interface UseRecipeUrlStateOptions {
  /** URL query param that carries the active recipe id. Domain-relevant, e.g. 'review', 'view', 'recipe'. */
  param?: string;
  /**
   * Re-assertion signal. A deep-linked recipe applies on mount, but domain
   * stores frequently (re)initialize afterwards — most notably when the
   * collaborative doc finishes loading — and replace the state the recipe
   * just wrote, leaving the URL claiming a view the canvas no longer shows.
   * Pass a value that changes when that happens (e.g. the store's doc
   * identity); the hook re-applies the active recipe unless the recipe
   * reports itself still applied via `isApplied`.
   */
  reapplyKey?: unknown;
}

interface RecipeUrlState {
  activeRecipeId: string | null;
  selectRecipe: (id: string | null) => void;
}

/**
 * Deep-linkable recipe selection. The active recipe id lives in the URL
 * query string so a recipe view is shareable: open the link and the hook
 * applies it on load. Selecting/clearing a recipe writes the param back.
 *
 * Universal: it knows nothing about how a recipe applies — it just calls
 * `recipe.apply()` / `recipe.clear()`. Each domain supplies its own
 * recipes and chooses the param name that fits its use cases.
 */
export function useRecipeUrlState(
  recipes: readonly ReviewRecipe[],
  options: UseRecipeUrlStateOptions = {},
): RecipeUrlState {
  const param = options.param ?? 'review';

  const readParam = useCallback((): string | null => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get(param);
  }, [param]);

  const [activeRecipeId, setActiveRecipeId] = useState<string | null>(() => {
    const urlRecipeId = readParam();
    if (urlRecipeId) return urlRecipeId;
    return recipes.find((recipe) => recipe.isApplied?.())?.id ?? null;
  });

  // Apply whenever the resolved active id changes (initial load + deep link
  // + explicit selection). Guarded by a ref so we apply exactly once per id.
  const appliedRef = useRef<string | null>(null);
  useEffect(() => {
    if (appliedRef.current === activeRecipeId) return;
    const previous = findRecipeById(recipes, appliedRef.current);
    const next = findRecipeById(recipes, activeRecipeId);
    if (previous && previous.id !== next?.id) previous.clear?.();
    appliedRef.current = activeRecipeId;
    if (next?.isApplied?.()) return;
    next?.apply();
  }, [activeRecipeId, recipes]);

  // Domain stores (re)initialize after mount — the collaborative doc arriving
  // replaces view state wholesale, racing the deep-link apply above. When the
  // caller signals such a reset, re-assert the active recipe so the canvas
  // matches what the URL claims. `isApplied` keeps this a no-op when the
  // recipe's state survived (or was persisted in the doc), so re-assertion
  // never churns shared state.
  const reapplyKey = options.reapplyKey;
  const lastReapplyKeyRef = useRef<unknown>(reapplyKey);
  useEffect(() => {
    if (Object.is(lastReapplyKeyRef.current, reapplyKey)) return;
    lastReapplyKeyRef.current = reapplyKey;
    // A persisted recipe can become applied when the collaborative document
    // arrives without a `?review=` URL parameter. Keep the picker in sync with
    // that state instead of presenting the default view as selected.
    if (activeRecipeId === null && readParam() === null) {
      const appliedRecipe = recipes.find((recipe) => recipe.isApplied?.());
      if (appliedRecipe) {
        setActiveRecipeId(appliedRecipe.id);
        return;
      }
    }
    const active = findRecipeById(recipes, appliedRef.current);
    if (!active || active.isApplied?.()) return;
    active.apply();
  }, [activeRecipeId, readParam, reapplyKey, recipes]);

  // Keep in sync with back/forward navigation.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => setActiveRecipeId(readParam());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [readParam]);

  const selectRecipe = useCallback(
    (id: string | null) => {
      setActiveRecipeId((current) => (current === id ? current : id));
      if (typeof window === 'undefined') return;
      const url = new URL(window.location.href);
      if (id) url.searchParams.set(param, id);
      else url.searchParams.delete(param);
      window.history.replaceState(window.history.state, '', url.toString());
    },
    [param],
  );

  return { activeRecipeId, selectRecipe };
}
